const TOKEN_EXPIRATION_BUFFER_MS = 30_000;
const VICTORY_LIVE_LOGIN_REDIRECT_KEY = 'victory-live-login-redirect';
const VICTORY_LIVE_SESSION_COOKIE_REQUIRED_MESSAGE = 'Session cookie is required.';

export interface VictoryLiveJwtPayload {
  sub?: number | string;
  email?: string;
  name?: string;
  username?: string;
  exp?: number;
  [key: string]: unknown;
}

export interface VictoryLiveExchangeResponse {
  Data?: {
    BearerToken?: string;
    Expiration?: string;
  };
}

export interface AuthUser {
  sub?: string;
  email?: string;
  name?: string;
  username?: string;
  [key: string]: unknown;
}

export class VictoryLiveExchangeError extends Error {
  responseMessage?: string;
  status?: number;

  constructor(message: string, status?: number, responseMessage?: string) {
    super(message);
    this.name = 'VictoryLiveExchangeError';
    this.responseMessage = responseMessage;
    this.status = status;
  }
}

function decodeBase64UrlValue(value: string) {
  const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
  const binary = window.atob(padded);
  const encoded = Array.from(binary)
    .map((character) => `%${character.charCodeAt(0).toString(16).padStart(2, '0')}`)
    .join('');

  return decodeURIComponent(encoded);
}

export function decodeVictoryLiveJwt(token: string): VictoryLiveJwtPayload {
  const [, payload] = token.split('.');

  if (!payload) {
    throw new Error('Victory Live bearer token is missing a JWT payload.');
  }

  return JSON.parse(decodeBase64UrlValue(payload)) as VictoryLiveJwtPayload;
}

export function getVictoryLiveTokenExpiryMs(token: string) {
  const payload = decodeVictoryLiveJwt(token);
  return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
}

export function isVictoryLiveTokenExpired(token?: string | null, expiresAt?: number | null, now = Date.now()) {
  if (!token) {
    return true;
  }

  const expiry = expiresAt ?? getVictoryLiveTokenExpiryMs(token);

  if (expiry === null || Number.isNaN(expiry)) {
    return true;
  }

  return expiry - TOKEN_EXPIRATION_BUFFER_MS <= now;
}

export function toAuthUser(payload: VictoryLiveJwtPayload): AuthUser {
  return {
    ...payload,
    sub: payload.sub === undefined ? undefined : String(payload.sub),
    email: typeof payload.email === 'string' ? payload.email : undefined,
    name: typeof payload.name === 'string' ? payload.name : undefined,
    username: typeof payload.username === 'string' ? payload.username : undefined,
  };
}

export function buildVictoryLiveReturnUrl(baseUrl: string, returnUrl: string) {
  const url = new URL(baseUrl);
  url.searchParams.set('returnUrl', returnUrl);
  return url.toString();
}

export function isVictoryLiveSessionCookieMissingError(error: unknown) {
  if (!(error instanceof VictoryLiveExchangeError) || error.status !== 400) {
    return false;
  }

  return (
    error.responseMessage === VICTORY_LIVE_SESSION_COOKIE_REQUIRED_MESSAGE ||
    error.message.includes(VICTORY_LIVE_SESSION_COOKIE_REQUIRED_MESSAGE)
  );
}

export function hasPendingVictoryLiveLoginRedirect(storage: Storage = window.sessionStorage) {
  return storage.getItem(VICTORY_LIVE_LOGIN_REDIRECT_KEY) === '1';
}

export function setPendingVictoryLiveLoginRedirect(storage: Storage = window.sessionStorage) {
  storage.setItem(VICTORY_LIVE_LOGIN_REDIRECT_KEY, '1');
}

export function clearPendingVictoryLiveLoginRedirect(storage: Storage = window.sessionStorage) {
  storage.removeItem(VICTORY_LIVE_LOGIN_REDIRECT_KEY);
}
